import Pusher, { Channel } from 'pusher-js'
import { addNotification, listNotifications } from '@/services/clientPortalNotifications'
import { getClientPortalFeatureFlags } from '@/utils/clientPortalFeatures'
import type { Contractor } from '@/services/contractorService'

export type ClientViolationAction = 'detected' | 'updated'

export interface ClientViolationPayload {
  action?: ClientViolationAction
  violation?: Record<string, unknown>
  [key: string]: unknown
}

export interface ClientViolationItem {
  id: string
  action: ClientViolationAction
  deviceId?: string
  vehicleName?: string
  message: string
  timestamp: string
  raw: ClientViolationPayload
}

type ViolationListener = (item: ClientViolationItem) => void

let pusher: Pusher | null = null
let channel: Channel | null = null
let activeChannelName = ''
const listeners = new Set<ViolationListener>()

const PUSHER_KEY = import.meta.env.VITE_PUSHER_APP_KEY || import.meta.env.VITE_PUSHER_KEY || '1e7707caf4d4eca34616'
const PUSHER_CLUSTER = import.meta.env.VITE_PUSHER_APP_CLUSTER || import.meta.env.VITE_PUSHER_CLUSTER || 'ap2'

function getClientIdFromStorage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem('contractor')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { id?: string | number }
    if (parsed?.id == null || parsed.id === '') return null
    return String(parsed.id)
  } catch {
    return null
  }
}

function createViolationItem(payload: ClientViolationPayload): ClientViolationItem {
  const violation = (payload.violation || {}) as Record<string, unknown>
  const action: ClientViolationAction = payload.action === 'updated' ? 'updated' : 'detected'
  const violationId = String(violation.id ?? `violation-${Date.now()}`)
  const deviceId = violation.device_id != null ? String(violation.device_id) : undefined
  const vehicleName =
    (typeof violation.vehicle_name === 'string' && violation.vehicle_name) ||
    (typeof violation.number_plate === 'string' && violation.number_plate) ||
    (typeof violation.license_plate === 'string' && violation.license_plate) ||
    undefined
  const ts =
    (typeof violation.created_at === 'string' && violation.created_at) ||
    (typeof violation.updated_at === 'string' && violation.updated_at) ||
    new Date().toISOString()

  const label = vehicleName || deviceId || 'vehicle'
  const message = action === 'updated' ? `Violation updated for ${label}` : `Violation detected for ${label}`

  return {
    id: `${violationId}:${action}`,
    action,
    deviceId,
    vehicleName,
    message,
    timestamp: ts,
    raw: payload,
  }
}

function notifyListeners(item: ClientViolationItem) {
  listeners.forEach((listener) => listener(item))
}

function vehicleViolationsEnabled(): boolean {
  if (typeof window === 'undefined') return true
  try {
    const raw = localStorage.getItem('contractor')
    if (!raw) return true
    const parsed = JSON.parse(raw) as Contractor
    return getClientPortalFeatureFlags(parsed).vehicleFeaturesEnabled
  } catch {
    return true
  }
}

function bindViolationEvents(targetChannel: Channel) {
  const handler = (payload: ClientViolationPayload) => {
    if (!vehicleViolationsEnabled()) return
    console.log('[ClientViolations] Event received:', payload)
    const item = createViolationItem(payload || {})

    addNotification({
      id: item.id,
      type: 'violation',
      title: item.action === 'updated' ? 'Vehicle violation updated' : 'Vehicle violation detected',
      message: item.message,
      createdAt: item.timestamp,
      meta: {
        ...item.raw,
        violationItem: item,
      },
    })

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('client-violation-event', { detail: item }))
    }
    notifyListeners(item)
  }

  // Raw Pusher event name (recommended in API docs)
  targetChannel.bind('client-violation', handler)
  // Compatibility if backend sends prefixed event name
  targetChannel.bind('.client-violation', handler)
}

function initRealtimeChannel() {
  if (typeof window === 'undefined') return
  if (!PUSHER_KEY || !PUSHER_CLUSTER) {
    console.warn('[ClientViolations] Missing Pusher config', {
      key: !!PUSHER_KEY,
      cluster: !!PUSHER_CLUSTER,
    })
    return
  }

  const clientId = getClientIdFromStorage()
  if (!clientId) {
    console.warn('[ClientViolations] No clientId found in localStorage contractor profile')
    return
  }

  const channelName = `client-violations-${clientId}`
  if (channel && activeChannelName === channelName) return

  if (!pusher) {
    console.log('[ClientViolations] Initializing Pusher', {
      cluster: PUSHER_CLUSTER,
      clientId,
    })
    pusher = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
    })

    pusher.connection.bind('state_change', (states: { previous: string; current: string }) => {
      console.log('[ClientViolations] Connection state:', states.previous, '->', states.current)
    })

    pusher.connection.bind('error', (error: unknown) => {
      console.error('[ClientViolations] Connection error:', error)
    })
  }

  if (channel) {
    channel.unbind_all()
    pusher?.unsubscribe(activeChannelName)
  }

  activeChannelName = channelName
  console.log('[ClientViolations] Subscribing to channel:', channelName)
  channel = pusher.subscribe(channelName)
  channel.bind('pusher:subscription_succeeded', () => {
    console.log('[ClientViolations] Subscription succeeded:', channelName)
  })
  channel.bind('pusher:subscription_error', (status: unknown) => {
    console.error('[ClientViolations] Subscription error:', channelName, status)
  })
  bindViolationEvents(channel)
}

export function subscribeToClientViolations(listener: ViolationListener): () => void {
  listeners.add(listener)
  initRealtimeChannel()

  return () => {
    listeners.delete(listener)
  }
}

export function startClientViolationsRealtime() {
  if (!vehicleViolationsEnabled()) return
  initRealtimeChannel()
}

export function stopClientViolationsRealtime() {
  if (channel) {
    channel.unbind_all()
    pusher?.unsubscribe(activeChannelName)
    channel = null
  }
  activeChannelName = ''
}

export function listViolationAlerts(): ClientViolationItem[] {
  if (!vehicleViolationsEnabled()) return []
  return listNotifications()
    .filter((n) => n.type === 'violation')
    .map((n) => {
      const meta = (n.meta || {}) as Record<string, unknown>
      const violationItem = meta.violationItem as ClientViolationItem | undefined
      if (violationItem) return violationItem
      return {
        id: n.id,
        action: 'detected',
        message: n.message,
        timestamp: n.createdAt,
        raw: meta as ClientViolationPayload,
      } as ClientViolationItem
    })
}
